import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Megaphone } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { usePatientNavigation } from '@/components/ui/PatientLink';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { usePageContext } from '@/hooks/usePageContext';
import { RecurringDetailModal } from '@/pages/memberships/RecurringDetailModal';
import {
  cancelRecurringPayment,
} from '@/services/memberships.service';
import {
  getMembershipManagementEnrollments,
  getMembershipManagementMembers,
  type MembershipManagementRow,
  type MembershipMemberRow,
} from '@/services/memberships-management.service';
import { useMembershipFilterOptions } from '@/hooks/filters';
import { buildApiParams, buildMarketingFilterConditions, extractSearch } from '@/lib/filterRegistry';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { useAuth } from '@/hooks/useAuth';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';

import {
  EMPTY_MEMBERSHIP_STATS,
  formatMembershipCurrency,
  formatMembershipDate,
  membershipAIContextTitle,
  MEMBERSHIP_FILTER_CONFIGS,
  membershipPageTitle,
  membershipRowClass,
  membershipStatTiles,
  membershipStatusDeepLink,
  membershipStatusLabel,
  MEMBERSHIP_STATUS_VARIANTS,
  type MembershipManagementMode,
} from './membershipsManagementShared';

export function MembershipsManagementList({
  mode,
  onModeChange,
}: {
  mode: MembershipManagementMode;
  onModeChange?: (mode: MembershipManagementMode) => void;
}) {
  const navToPatient = usePatientNavigation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext(membershipAIContextTitle(mode));

  const filterState = useLocalDashboardFilters(MEMBERSHIP_FILTER_CONFIGS, 'memberships');

  // A `?status=` deep link — the dashboard's Recent Cancellations card is what
  // sends one — seeds the shared 'memberships' filter bucket, which is where this
  // list reads Status from. Without this the param was simply dropped and the
  // link landed on the unfiltered list.
  //
  // Only a token that names a status seeds. /memberships/all with no param is
  // also the plain nav destination, and clearing Status there would throw away a
  // filter the user set for themselves. The param stays in the URL so a reload
  // still honours it; once seeded, the filter is the sidebar's to change or clear.
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const statusSeed = membershipStatusDeepLink(statusParam);
  const [seededStatusParam, setSeededStatusParam] = useState<string | null>(null);

  // True for the render between arriving on a deep link and the seed reaching
  // the bucket. The queries below sit out that render: firing them would request
  // — and paint — the unfiltered list first, which is the very bug this fixes.
  const awaitingStatusSeed = statusSeed !== null && seededStatusParam !== statusParam;

  useEffect(() => {
    if (statusSeed) filterState.setFilter('membership-status', statusSeed);
    setSeededStatusParam(statusParam);
  }, [statusParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [detailRow, setDetailRow] = useState<MembershipManagementRow | null>(null);
  const [cancelRow, setCancelRow] = useState<MembershipManagementRow | null>(null);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const {
    exporting: exportingMemberships,
    startExport: startMembershipExport,
    exportModal: membershipsExportModal,
  } = useAsyncExport({
    entityType: 'memberships',
    entityLabel: 'memberships',
  });
  const {
    exporting: exportingMembers,
    startExport: startMemberExport,
    exportModal: membersExportModal,
  } = useAsyncExport({
    entityType: 'members',
    entityLabel: 'members',
  });
  const exporting = exportingMemberships || exportingMembers;

  const baseFilters = useMemo(
    () => ({ page, perPage, sortBy, sortDir }),
    [page, perPage, sortBy, sortDir],
  );

  const effectiveFilters = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations, baseFilters),
    [baseFilters, effectiveValues, filterState.negations],
  );

  const search = extractSearch(effectiveValues);
  const activeFilters = useMemo(
    () => Object.entries(effectiveValues)
      .filter(([key, val]) => {
        if (key === 'search') return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
        return false;
      })
      .map(([key]) => key)
      .join(', '),
    [effectiveValues],
  );

  const marketingFilterConditions = useMemo(
    () => buildMarketingFilterConditions(
      mode === 'memberships' ? 'membership' : 'member',
      effectiveValues,
      filterState.negations,
    ),
    [effectiveValues, filterState.negations, mode],
  );

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [effectiveValues]);

  const sidebarOptions = useMembershipFilterOptions();

  const membershipListQuery = useQuery({
    queryKey: ['membership-management', 'memberships', effectiveFilters],
    queryFn: () => getMembershipManagementEnrollments(effectiveFilters),
    enabled: mode === 'memberships' && !awaitingStatusSeed,
  });

  const memberListQuery = useQuery({
    queryKey: ['membership-management', 'members', effectiveFilters],
    queryFn: () => getMembershipManagementMembers(effectiveFilters),
    enabled: mode === 'members' && !awaitingStatusSeed,
  });

  const cancelMutation = useMutation({
    mutationFn: (row: MembershipManagementRow) => {
      if (!row.recurringPaymentId) {
        throw new Error('Recurring payment ID is required to cancel this membership.');
      }

      return cancelRecurringPayment(row.recurringPaymentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membership-management'] });
    },
  });

  const result = mode === 'memberships' ? membershipListQuery.data : memberListQuery.data;
  const listQuery = mode === 'memberships' ? membershipListQuery : memberListQuery;
  const membershipRows = mode === 'memberships'
    ? (membershipListQuery.data?.items ?? [])
    : [];
  const memberRows = mode === 'members'
    ? (memberListQuery.data?.items ?? [])
    : [];


  const stats = result?.stats ?? EMPTY_MEMBERSHIP_STATS;
  const statTiles = useMemo(() => membershipStatTiles(mode, stats), [mode, stats]);

  useEffect(() => {
    const filterSummary = [
      search ? `Search: "${search}"` : null,
      activeFilters ? `Filters: ${activeFilters}` : null,
    ].filter(Boolean).join(', ');

    updateAIContext({
      filters: filterSummary || 'No filters applied',
      dataSummary: `${result?.pagination.total ?? 0} results (${stats.addedCount} added in range, ${stats.cancelledCount} cancelled in range, ${stats.frozenCount} frozen, ${stats.declinedCount} past due)`,
    });
  }, [activeFilters, result?.pagination.total, stats, search, updateAIContext]);

  const renderDate = (value: string | null | undefined) => {
    const d = formatMembershipDate(value);
    if (!d) return <span className="text-xs text-slate-400">{'\u2014'}</span>;
    return (
      <div className="leading-tight">
        <p className="text-xs text-slate-700">{d.monthDay}</p>
        <p className="text-[11px] text-slate-400">{d.year}</p>
      </div>
    );
  };

  const membershipColumns = useMemo<DataTableColumn<MembershipManagementRow>[]>(() => [
    { key: 'createdAt', header: 'Date', sortable: true, render: (row) => renderDate(row.createdAt) },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      width: '220px',
      render: (row) => <span className="font-medium text-slate-800">{row.patientName}</span>,
    },
    { key: 'type', header: 'Type', sortable: true, render: (row) => <span className="text-xs text-slate-700">{row.type || '\u2014'}</span> },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (row) => <span className="text-xs font-medium text-slate-800">{formatMembershipCurrency(row.amount)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <Badge variant={MEMBERSHIP_STATUS_VARIANTS[row.status] ?? 'neutral'} dot>
          {membershipStatusLabel(row.status)}
        </Badge>
      ),
    },
    { key: 'notes', header: 'Notes', sortable: true, render: (row) => <span className="text-xs text-slate-600">{row.notes || '\u2014'}</span> },
    {
      key: 'nextChargeDate',
      header: 'Next Charge',
      sortable: true,
      render: (row) => renderDate(row.nextChargeDate),
    },
    {
      key: 'userName',
      header: 'Created By',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-700">{row.userName || '\u2014'}</span>,
    },
  ], []);

  const membershipActions = useMemo<DataTableAction<MembershipManagementRow>[]>(() => [
    { label: 'View Details', onClick: (row) => setDetailRow(row) },
    { label: 'View Patient', onClick: (row) => navToPatient({ patientId: row.patientId, name: row.patientName }) },
    {
      label: 'Cancel Membership',
      variant: 'danger',
      onClick: (row) => setCancelRow(row),
      disabled: (row) => row.status === 'cancelled' || row.status === 'cancelling' || !row.recurringPaymentId,
    },
  ], [navToPatient]);

  const memberColumns = useMemo<DataTableColumn<MembershipMemberRow>[]>(() => [
    { key: 'createdAt', header: 'Create Date', sortable: true, render: (row) => renderDate(row.createdAt) },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      width: '220px',
      render: (row) => <span className="font-medium text-slate-800">{row.patientName}</span>,
    },
    { key: 'memberships', header: 'Memberships', sortable: true, render: (row) => <span className="text-xs text-slate-700">{row.memberships || '\u2014'}</span> },
    { key: 'startDate', header: 'Start Date', sortable: true, render: (row) => renderDate(row.startDate) },
    { key: 'paidUntilDate', header: 'Paid Until', sortable: true, render: (row) => renderDate(row.paidUntilDate) },
    { key: 'cancelDate', header: 'Cancel Date', sortable: true, render: (row) => renderDate(row.cancelDate) },
    { key: 'daysActive', header: 'Days Active', sortable: true, render: (row) => <span className="text-xs text-slate-700">{row.daysActive}</span> },
  ], []);

  function handleExport() {
    const filters = effectiveFilters as unknown as Record<string, unknown>;
    if (mode === 'memberships') {
      startMembershipExport(filters);
    } else {
      startMemberExport(filters);
    }
  }

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={MEMBERSHIP_FILTER_CONFIGS}
            values={effectiveValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={filterState.setFilter}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="memberships"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6 max-md:pb-16">
            {/* SCRUM-1451. This strip used to be passed as PageHeader's `title`, which renders
                it inside `<h1 className="truncate …">`. Three consequences, all reported:
                `truncate` is `overflow: hidden`, so the strip's right border was clipped by the
                heading; the selected button had no rounding of its own and painted square into
                the container's `rounded-md`; and two content-sized `px-4` buttons in a
                block-level flex row are inherently unequal, so "Members" and "Memberships"
                never matched. It also nested interactive buttons inside a heading, and
                `PageHeaderProps.title` is typed `string` — the ReactNode being passed was a
                live typecheck error.

                Out into its own row: `w-fit` so the strip is its content and not the line,
                `overflow-hidden` so the selected fill follows the border radius, and
                `max-md:flex-1` on each button so the two halves are equal on a phone only. */}
            {onModeChange && (
              <div className="flex w-fit overflow-hidden rounded-md border border-slate-200 bg-white text-sm max-md:w-full">
                {(['memberships', 'members'] as MembershipManagementMode[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => onModeChange(tab)}
                    className={
                      tab === mode
                        ? 'bg-primary px-4 py-2 font-medium text-white max-md:flex-1'
                        : 'px-4 py-2 text-slate-600 hover:text-slate-800 max-md:flex-1'
                    }
                  >
                    {tab === 'memberships' ? 'Memberships' : 'Members'}
                  </button>
                ))}
              </div>
            )}
            <PageHeader
              variant="compact"
              title={membershipPageTitle(mode)}
              actions={(
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    disabled={(result?.pagination.total ?? 0) === 0 || exporting}
                    loading={exporting}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  {membershipsExportModal}
                  {membersExportModal}
                  {hasPermission(20) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Megaphone className="h-4 w-4" />}
                      onClick={() => setMarketingModalOpen(true)}
                    >
                      Email / SMS List
                    </Button>
                  )}
                </>
              )}
            />

            <MetricCardGrid columns={5}>
              {statTiles.map((tile) => (
                <StatCard key={tile.key} label={tile.label} value={tile.value} tone={tile.tone} />
              ))}
            </MetricCardGrid>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {mode === 'memberships' ? (
              <DataTable<MembershipManagementRow>
                data={membershipRows}
                columns={membershipColumns}
                keyField="enrollmentId"
                loading={listQuery.isLoading || awaitingStatusSeed}
                rowActions={membershipActions}
                onRowClick={(row) => setDetailRow(row)}
                rowClassName={membershipRowClass}
                page={result?.pagination.page ?? page}
                pageSize={result?.pagination.perPage ?? perPage}
                total={result?.pagination.total ?? 0}
                onPageChange={setPage}
                manualSort
                sortKey={result?.sort.field ?? sortBy}
                sortDir={result?.sort.direction ?? sortDir}
                onSortChange={(key, direction) => {
                  setSortBy(key);
                  setSortDir(direction);
                  setPage(1);
                }}
                emptyTitle="No memberships found"
                emptyDescription="Try adjusting the management filters."
              />
            ) : (
              <DataTable<MembershipMemberRow>
                data={memberRows}
                columns={memberColumns}
                keyField="patientId"
                loading={listQuery.isLoading || awaitingStatusSeed}
                onRowClick={(row) => navToPatient({ patientId: row.patientId, name: row.patientName })}
                page={result?.pagination.page ?? page}
                pageSize={result?.pagination.perPage ?? perPage}
                total={result?.pagination.total ?? 0}
                onPageChange={setPage}
                manualSort
                sortKey={result?.sort.field ?? sortBy}
                sortDir={result?.sort.direction ?? sortDir}
                onSortChange={(key, direction) => {
                  setSortBy(key);
                  setSortDir(direction);
                  setPage(1);
                }}
                emptyTitle="No members found"
                emptyDescription="Try adjusting the management filters."
              />
            )}
          </div>
        </div>
      </ListPageLayout>

      {mode === 'memberships' && detailRow && (
        <RecurringDetailModal
          recurringId={detailRow.recurringPaymentId ?? detailRow.enrollmentId}
          open={!!detailRow}
          onClose={() => setDetailRow(null)}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['membership-management'] });
          }}
        />
      )}

      {mode === 'memberships' && (
        <ConfirmDialog
          isOpen={cancelRow !== null}
          onClose={() => setCancelRow(null)}
          onConfirm={async () => {
            if (cancelRow) {
              await cancelMutation.mutateAsync(cancelRow);
              setCancelRow(null);
            }
          }}
          title="Cancel Membership"
          message={cancelRow ? `Cancel ${cancelRow.type} for ${cancelRow.patientName}?` : ''}
          confirmLabel="Cancel Membership"
          cancelLabel="Keep Active"
          variant="danger"
        />
      )}

      {hasPermission(20) && (
        <MarketingActionsModal
          isOpen={marketingModalOpen}
          onClose={() => setMarketingModalOpen(false)}
          sourceType={mode === 'memberships' ? 'membership' : 'member'}
          filterConditions={marketingFilterConditions}
        />
      )}
    </>
  );
}
